import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, makeStateKey, StateKey, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  forkJoin,
  merge,
  Observable,
  of,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { MultivariateTestService } from '@fcom/common/multivariate-test/services/multivariate-test.service';
import { AppState, ConfigService, DdsLanguage, MonitoringLogger, StateTransferService } from '@fcom/core';
import { CmsContent, CmsContentType, CmsTemplate, RESPONSE_CODE_HANDLER, ResponseCodeHandler } from '@fcom/core-api';
import { Profile, PersonalizationParameterProfileFlagName } from '@fcom/core-api/login';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { finShare, retryWithBackoff } from '@fcom/rx';
import { LanguageService } from '@fcom/ui-translate';
import { getWhitelistedCmsParamsMapped, pathToTemplateUrl, stringHashCode } from '@fcom/core/utils';
import { CmsPersonalizationParameter, CreditCardPersonalizationParameter } from '@fcom/core-api/interfaces';

import {
  finUniversalResponseStatus,
  hasPersonalizedContent,
  parseCmsTemplate,
  recursiveParse,
} from '../cms-utils/cms-utils';

interface CmsPageTemplateWithPath {
  path: string;
  template: CmsTemplate;
}

// @TODO: We should have proper static error page rendered
export const error404template: CmsTemplate = {
  header: [],
  main: [],
  footer: [],
};

@Injectable()
export class CmsTemplateService {
  private http = inject(HttpClient);
  private config = inject(ConfigService);
  private monitoringLogger = inject(MonitoringLogger);
  private serverResponseCodeHandler = inject<ResponseCodeHandler>(RESPONSE_CODE_HANDLER, { optional: true });
  private stateTransferService = inject(StateTransferService);
  private languageService = inject(LanguageService);
  private multivariateTestService = inject(MultivariateTestService);
  private store$ = inject<Store<AppState>>(Store);

  private segment$: Observable<number>;
  private currentCmsPageTemplate$: BehaviorSubject<CmsPageTemplateWithPath | undefined> = new BehaviorSubject<
    CmsPageTemplateWithPath | undefined
  >(undefined);
  static createStateCacheKey = (templateUrl: string): StateKey<CmsTemplate> =>
    makeStateKey<CmsTemplate>(`cts-${stringHashCode(templateUrl)}`);
  static createP13CacheKey = (contentUrl: string): StateKey<CmsContent> =>
    makeStateKey<CmsContent>(`p13-${stringHashCode(contentUrl)}`);

  constructor() {
    this.segment$ = this.multivariateTestService.segmentsFeed$.pipe(map((value) => value.segment));
  }

  /**
   * Load the template for the given path in CMS.
   * <p>
   * Translates path
   * <tt>/en/destination/germany/berlin</tt>
   * to e.g.
   * <tt>http://test.coremedia.dev.app.finnair.com/en/destinations/europe/germany/berlin?view=fin-json</tt>
   *
   * @param path the path to retrieve
   * @returns {Observable<string>} the template as Observable
   */
  load(path: string): Observable<CmsTemplate> {
    const defaultTemplate$ = this.loadOnce(path);
    const personalisedTemplate$ = this.getP13NTemplate(defaultTemplate$);

    // NOTE: there is race here so ensure that defaultTemplate$ will always emit ones first and
    // personalisedTemplate$ comes later or not
    return merge(
      defaultTemplate$.pipe(
        map((template) => ({
          ...template,
          main: template.main.filter(
            (item) =>
              !(item.contentType === CmsContentType.CMPersonalized && item.personalizationParameters?.length > 0)
          ),
        }))
      ),
      personalisedTemplate$
    ).pipe(tap((template) => this.currentCmsPageTemplate$.next({ path, template })));
  }

  getTemplateForPathIfExists(path: string): Observable<CmsTemplate | undefined> {
    return this.currentCmsPageTemplate$.pipe(
      distinctUntilChanged((a, b) => a?.path === b?.path),
      map((currentPageTemplate) => (currentPageTemplate?.path === path ? currentPageTemplate.template : undefined))
    );
  }

  private loadOnce(path: string): Observable<CmsTemplate> {
    const templateUrl = pathToTemplateUrl(this.config.cmsUrl, path);
    const stateKey = CmsTemplateService.createStateCacheKey(templateUrl);
    return this.stateTransferService.wrapToStateCache(stateKey, () => this.getTemplate(templateUrl).pipe(finShare()));
  }

  private getTemplate(templateUrl: string): Observable<CmsTemplate> {
    return this.http
      .get<CmsTemplate>(templateUrl, {
        withCredentials: this.config.cfg.enableCorsCredentials,
        observe: 'response',
      })
      .pipe(
        retryWithBackoff(1),
        finUniversalResponseStatus(this.serverResponseCodeHandler, 'CmsTemplateService', templateUrl),
        map((res) => res.body),
        map((data: CmsTemplate) =>
          parseCmsTemplate(data, this.languageService.ddsLocaleValue as unknown as DdsLanguage, this.monitoringLogger)
        ),

        catchError((error: unknown) => {
          const httpError = error as HttpErrorResponse;
          const statusCode = httpError.status || 500;

          if (statusCode !== 404) {
            this.monitoringLogger.error('Internal server error', { error: httpError });
          }
          if (this.serverResponseCodeHandler) {
            this.serverResponseCodeHandler.setUniversalResponseStatus('CmsTemplateService', statusCode, templateUrl);
          }
          const errorTemplateFromError: CmsTemplate | undefined = (httpError || {}).error;
          const errorTemplate = errorTemplateFromError?.main ? errorTemplateFromError : error404template;
          return of({ ...errorTemplate, status: statusCode });
        })
      );
  }

  private fetchCustomizedContent(contentUrl: string, originalContent: CmsContent): Observable<CmsContent> {
    return this.http
      .get<CmsContent>(contentUrl, {
        withCredentials: this.config.cfg.enableCorsCredentials,
        observe: 'response',
      })
      .pipe(
        retryWithBackoff(1),
        finUniversalResponseStatus(this.serverResponseCodeHandler, 'CmsTemplateService', contentUrl),
        map((res) => recursiveParse(res.body, this.monitoringLogger)),
        catchError((error: unknown) => {
          this.monitoringLogger.error('Internal server error', { error });
          // Return original content block in case of error so that stream won't be broken
          return of(originalContent);
        })
      );
  }

  private getP13NTemplate(template$: Observable<CmsTemplate>): Observable<CmsTemplate> {
    return combineLatest([
      template$,
      this.store$.pipe(profileOrUndefinedIfNotLoggedIn()),
      this.segment$.pipe(take(1)),
    ]).pipe(
      filter(([template]) => hasPersonalizedContent(template.main)),
      switchMap(([template, profileValue, segment]) => {
        const items$: Array<Observable<CmsContent>> = template.main.map((item) =>
          this.getPersonalizedContentItem(item, profileValue, segment)
        );
        return forkJoin([...items$]).pipe(map((items) => ({ ...template, main: items })));
      }),
      finShare()
    );
  }

  private getPersonalizedContentItem(
    contentItem: CmsContent,
    profileValue: Profile,
    segment: number
  ): Observable<CmsContent> {
    if (contentItem.contentType !== CmsContentType.CMPersonalized) {
      const children$ = (contentItem.items ?? []).map((item) =>
        this.getPersonalizedContentItem(item, profileValue, segment)
      );
      return children$.length > 0
        ? forkJoin([...children$]).pipe(map((children: CmsContent[]) => ({ ...contentItem, items: children })))
        : of(contentItem);
    }

    if (!contentItem.personalizationParameters?.length) {
      return of(contentItem);
    }

    const personalizationSearchParams = contentItem.personalizationParameters;
    const profileTierParam = personalizationSearchParams.includes(CmsPersonalizationParameter.TIER)
      ? `tier=${profileValue?.tier ?? ''}`
      : '';
    const segmentGroupParam = personalizationSearchParams.includes(CmsPersonalizationParameter.GROUP)
      ? `group=${segment}`
      : '';
    const creditCardParamKey = Object.keys(CreditCardPersonalizationParameter).find((key) =>
      personalizationSearchParams.includes(CreditCardPersonalizationParameter[key])
    );

    const creditCardPersonalizationParameterValue = CreditCardPersonalizationParameter[creditCardParamKey];
    const creditCardPersonalizationParameterFlagName = PersonalizationParameterProfileFlagName[creditCardParamKey];
    const creditCardParam =
      creditCardParamKey && creditCardPersonalizationParameterFlagName
        ? `${creditCardPersonalizationParameterValue}=${profileValue?.[creditCardPersonalizationParameterFlagName] ? 'true' : 'false'}`
        : '';
    const path = contentItem.url;
    const oldQueryParams = getWhitelistedCmsParamsMapped(path);

    const allPersonalizationParams = oldQueryParams
      .concat(profileTierParam, segmentGroupParam, creditCardParam)
      .filter(Boolean)
      .join('&');

    const pathWithoutParams = `/${path}`.replace(/\?.*$/, '').replace(/^\/\//g, '/');

    const url = pathToTemplateUrl(
      this.config.cmsUrl,
      `${pathWithoutParams}?${allPersonalizationParams}`,
      'fin-p13n-json'
    );

    const stateKey = CmsTemplateService.createP13CacheKey(url);
    return this.stateTransferService
      .wrapToStateCache(stateKey, () => this.fetchCustomizedContent(url, contentItem))
      .pipe(finShare());
  }
}
